import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import FeatureNotEnabled from '../components/FeatureNotEnabled';
import { featureFlagsService } from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { 
  Package, 
  AlertCircle,
  AlertTriangle,
  MapPin,
  DollarSign,
  Building2,
  CheckCircle
} from 'lucide-react';

/**
 * Load Boards Module (Phase 6 - Scaffolded)
 * 
 * FEATURE FLAG: load_boards_enabled
 * ACCESS: Super Admin + Admin only
 * STATUS: Placeholder - No external API calls
 */
const LoadBoardsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  const allowedRoles = ['admin', 'super_admin', 'ops_admin'];

  useEffect(() => {
    if (user && !allowedRoles.includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const checkFeature = async () => {
      try {
        const enabledFeatures = await featureFlagsService.getEnabledFeatures();
        setFeatureEnabled(enabledFeatures.includes('load_boards_enabled'));
      } catch (err) {
        console.error('Failed to check feature flag:', err);
        setFeatureEnabled(false);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      checkFeature();
    }
  }, [token]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!featureEnabled) {
    return <FeatureNotEnabled featureName="Load Boards" />;
  }

  // Placeholder content when feature IS enabled (future-ready scaffold)
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {/* Breadcrumb */}
        <Breadcrumb items={[{ label: 'Load Boards' }]} />

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="w-8 h-8 text-green-600" />
            Load Boards
          </h1>
          <p className="text-gray-600 mt-1">External load board integrations and load management</p>
        </div>

        {/* Integration Warning Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-800">Integrations Not Configured</div>
            <div className="text-sm text-amber-700">
              External load board connections (DAT, Truckstop, etc.) are not configured. 
              This is a preview of the interface only.
            </div>
          </div>
        </div>

        {/* Preview Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-blue-800">Preview Mode</div>
            <div className="text-sm text-blue-700">
              No external API calls are made. Data shown below is placeholder only.
            </div>
          </div>
        </div>

        {/* Placeholder Table */}
        <Card>
          <CardHeader>
            <CardTitle>Available Loads</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Load ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Origin</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Destination</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Broker</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* Empty state placeholder rows */}
                  {[1, 2, 3].map((i) => (
                    <tr key={i} className="opacity-50">
                      <td className="px-4 py-4">
                        <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-gray-300" />
                          <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-gray-300" />
                          <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4 text-gray-300" />
                          <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          <Building2 className="w-4 h-4 text-gray-300" />
                          <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-center py-8 text-gray-400">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No loads available - Integrations not configured</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default LoadBoardsPage;
